'use strict';

/* eslint-disable @typescript-eslint/no-var-requires */

// Public facade for browser uploads. Provider-specific HTTP protocols live in
// ./browser-upload/{gcs,s3}.js; this file owns only lifecycle orchestration and
// keeps the import surface used by the Pinia store stable.

const {
  BROWSER_UPLOAD_STRATEGIES,
  UploadHttpError,
  applyUploadAuthorization,
  defaultFetchImpl,
  isAbortError,
  throwIfAborted,
  uploadErrorMessage,
} = require('./browser-upload/core');
const { runGcsResumable } = require('./browser-upload/gcs');
const { runS3Multipart, runS3SinglePart } = require('./browser-upload/s3');

async function restartAbortedUpload(upload) {
  // Aborted uploads have no reusable authority. Reauthorization moves the
  // backend record back to PENDING and supplies a new provider session.
  const authorization = await upload.api.authorize(upload.record.id, []);
  applyUploadAuthorization(upload, authorization);
  upload.record.status = authorization.upload?.status || 'PENDING';
  upload.record.progress = authorization.upload?.progress ?? 0;
}

async function runBrowserUpload({
  upload,
  file,
  api,
  onProgress = () => undefined,
  fetchImpl,
  signal,
  maxRetries = 3,
  retryDelayMs = 250,
}) {
  if (!upload?.id) throw new Error('Upload ID is missing.');

  // Validate before touching a signed URL. This prevents a user from resuming
  // an existing record with a different local file that merely shares its name.
  if (Number(upload.size) !== file.size) {
    throw new Error(
      `Selected file size ${file.size} does not match expected size ${upload.size}.`
    );
  }

  const state = { record: upload, file, api, onProgress };
  const options = {
    fetchImpl: fetchImpl || defaultFetchImpl(),
    signal,
    maxRetries,
    retryDelayMs,
  };

  throwIfAborted(signal);

  if (upload.status === 'ABORTED') {
    await restartAbortedUpload(state);
    throwIfAborted(signal);
  }

  // A provider may complete reconciliation itself (the S3 create-only retry
  // case). Otherwise the common path finalizes the backend record exactly once.
  let providerCompleted = null;
  if (upload.uploadStrategy === BROWSER_UPLOAD_STRATEGIES.GCS_RESUMABLE) {
    await runGcsResumable(state, options);
  } else if (
    upload.uploadStrategy === BROWSER_UPLOAD_STRATEGIES.S3_SINGLE_PART
  ) {
    providerCompleted = await runS3SinglePart(state, options);
  } else if (upload.uploadStrategy === BROWSER_UPLOAD_STRATEGIES.S3_MULTIPART) {
    await runS3Multipart(state, options);
  } else {
    throw new Error(
      `Unsupported browser upload strategy '${upload.uploadStrategy}'.`
    );
  }

  if (providerCompleted) {
    onProgress({ uploaded: file.size, total: file.size, done: true });
    return providerCompleted;
  }

  throwIfAborted(signal);
  let completed;
  try {
    completed = await api.complete(upload.id);
  } catch (error) {
    // If cancellation won the race, surface AbortError rather than a stale
    // completion mutation failure from the backend.
    throwIfAborted(signal);
    throw error;
  }
  throwIfAborted(signal);
  onProgress({ uploaded: file.size, total: file.size, done: true });
  return completed;
}

async function cancelBrowserUpload({ upload, api }) {
  // The API owns provider cleanup. In particular, it cancels GCS's bearer
  // session URI server-side before it persists the terminal upload state.
  return api.abort(upload.id);
}

module.exports = {
  BROWSER_UPLOAD_STRATEGIES,
  UploadHttpError,
  cancelBrowserUpload,
  isAbortError,
  runBrowserUpload,
  uploadErrorMessage,
};
